/**
 * The `Set <label>…` text an empty row stands in for its value with — derived
 * once, here, for every place in this package that builds it (VALE-130).
 *
 * ## Why it is sentence case
 *
 * The placeholder is a **sentence**, and its subject is the label. `Set Death
 * certificate number…` capitalises mid-sentence, and on a card where most rows
 * are unset — the ordinary state of a matter that has just been opened — that
 * is every row at once. VALE-91 caught it on the Overview estate-details card;
 * it was never specific to that card, because the string is derived from
 * `AttributeSpec.label` in shared code.
 *
 * **So the fix is here and not on a spec.** A per-field `placeholder` override
 * would be exactly the shape `components/attributes/CLAUDE.md` §2 refuses — a
 * shared component whose copy depends on each call site remembering a prop, and
 * therefore answers differently on each surface. There is no field for which
 * mid-sentence capitals are right, so there is no exception to name: derive the
 * default correctly instead.
 *
 * ## Why not `label.toLowerCase()`
 *
 * Seven shipped labels open with an acronym — `ABN`, `ACN`, `BSB`, `HIN
 * Portfolio`, `LPR`, `SRN`, `SRN Shares` — and lowercasing either the whole
 * label or just its first character mangles all of them (`Set bsb…`,
 * `Set bSB…`). `attribute-field-control.tsx` did carry a bare `.toLowerCase()`
 * for the *editing* placeholder, which is where `Set bsb…` came from and why
 * the same field read two different ways at rest and open; it binds here now,
 * so both agree and neither is wrong.
 *
 * The rule is therefore narrow and stated rather than clever: decapitalise the
 * first character **only when the label's first word is not itself an
 * acronym** — a run of two or more capitals. A one-letter word is left alone
 * for the same reason (`I`, an initial), and a label that already opens
 * lowercase is returned unchanged.
 */

/** A leading run of 2+ capitals — `BSB`, `SRN Shares`, `HIN Portfolio`. */
const OPENS_WITH_AN_ACRONYM = /^[A-Z]{2,}\b/;

/**
 * The label as it reads *inside* a sentence — `Death certificate number`
 * becomes `death certificate number`, `BSB` stays `BSB`.
 *
 * Exported for the label column's own use? No — deliberately not. The label
 * column renders the label as written, in title case; this is only ever the
 * subject of the `Set …` sentence, which is why it lives beside the string that
 * uses it rather than in `attribute-tokens.ts` with the class tokens.
 */
function inSentence(label: string): string {
  if (OPENS_WITH_AN_ACRONYM.test(label)) return label;
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/** The grey stand-in an empty row renders in place of its value. */
export function attributePlaceholder(label: string): string {
  return `Set ${inSentence(label)}…`;
}

/**
 * What an empty **read-only** row stands in with instead.
 *
 * `Set client…` is an instruction, and a `readOnly` row cannot be acted on — the
 * edit lives on another surface entirely, so the sentence would send the reader
 * to click a span that does nothing. An em dash makes the only claim this row is
 * entitled to make: *nothing is recorded*.
 */
export const ATTRIBUTE_NOT_RECORDED = '—';
